/**
 * Natural Language Recurrence Parser
 * Parses human-readable strings into RecurrenceConfig objects.
 *
 * Supported patterns (English + Swedish):
 * - "every day" / "daily" / "varje dag"
 * - "every weekday" / "varje vardag"
 * - "every monday" / "every mon, wed, fri"
 * - "every 2 days" / "var 2:a dag"
 * - "every week" / "weekly" / "varje vecka"
 * - "every 2 weeks" / "varannan vecka"
 * - "every month" / "monthly" / "varje månad"
 * - "every 3 months" / "quarterly"
 * - "every year" / "yearly" / "varje år"
 * - "3 times a week" / "3 gånger i veckan"
 * - "after 2 days" / "2 dagar efter"
 * - Time: "at 9", "at 9:30", "at 09:00", "kl 9", "kl 09:30"
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ParsedRecurrence {
  mode: "FIXED" | "AFTER_COMPLETION"
  frequency: "daily" | "weekly" | "monthly" | "yearly"
  interval: number
  weekdays?: number[] // 0=Mon..6=Sun
  time?: string // "09:00"
  /** Flexible: N times per period */
  flexibleCount?: number
  flexiblePeriod?: "week" | "month"
  /** After-completion days */
  afterCompletionDays?: number
  /** Raw confidence (0-1) */
  confidence: number
}

// ---------------------------------------------------------------------------
// Main Parser
// ---------------------------------------------------------------------------

/**
 * Parse a natural language string into a recurrence config.
 * Returns null if the input can't be parsed.
 */
export function parseRecurrenceNL(input: string): ParsedRecurrence | null {
  const text = input.trim().toLowerCase()
  if (!text) return null

  // Extract time first (remove from further parsing)
  const { time, remaining } = extractTime(text)

  // Try each pattern in priority order
  const result =
    tryAfterCompletion(remaining) ??
    tryFlexible(remaining) ??
    tryDaily(remaining) ??
    tryWeekday(remaining) ??
    tryWeekly(remaining) ??
    tryMonthly(remaining) ??
    tryYearly(remaining) ??
    tryEveryNDays(remaining) ??
    tryEveryNWeeks(remaining) ??
    tryEveryNMonths(remaining) ??
    trySpecificDays(remaining)

  if (!result) return null

  if (time) result.time = time
  return result
}

// ---------------------------------------------------------------------------
// Time extraction
// ---------------------------------------------------------------------------

function extractTime(text: string): { time: string | undefined; remaining: string } {
  // "at 9:30", "at 09", "at 9am", "kl 9:30", "kl. 09:00"
  const timeRegex = /(?:at|kl\.?)\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i
  const match = text.match(timeRegex)
  if (!match) return { time: undefined, remaining: text }

  let hours = parseInt(match[1]!)
  const minutes = match[2] ? parseInt(match[2]) : 0
  const ampm = match[3]?.toLowerCase()

  if (ampm === "pm" && hours < 12) hours += 12
  if (ampm === "am" && hours === 12) hours = 0

  const time = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`
  const remaining = text.replace(timeRegex, "").trim()
  return { time, remaining }
}

// ---------------------------------------------------------------------------
// Pattern matchers
// ---------------------------------------------------------------------------

function tryDaily(text: string): ParsedRecurrence | null {
  if (/^(every\s*day|daily|varje\s*dag|dagligen)$/.test(text)) {
    return { mode: "FIXED", frequency: "daily", interval: 1, confidence: 1.0 }
  }
  return null
}

function tryWeekday(text: string): ParsedRecurrence | null {
  if (/^(every\s*weekday|weekdays|varje\s*vardag|vardagar)$/.test(text)) {
    return {
      mode: "FIXED",
      frequency: "weekly",
      interval: 1,
      weekdays: [0, 1, 2, 3, 4], // Mon-Fri
      confidence: 1.0,
    }
  }
  return null
}

function tryWeekly(text: string): ParsedRecurrence | null {
  if (/^(every\s*week|weekly|varje\s*vecka|veckovis)$/.test(text)) {
    return { mode: "FIXED", frequency: "weekly", interval: 1, confidence: 1.0 }
  }
  return null
}

function tryMonthly(text: string): ParsedRecurrence | null {
  if (/^(every\s*month|monthly|varje\s*månad|månadsvis)$/.test(text)) {
    return { mode: "FIXED", frequency: "monthly", interval: 1, confidence: 1.0 }
  }
  return null
}

function tryYearly(text: string): ParsedRecurrence | null {
  if (/^(every\s*year|yearly|annually|varje\s*år|årligen)$/.test(text)) {
    return { mode: "FIXED", frequency: "yearly", interval: 1, confidence: 1.0 }
  }
  return null
}

function tryEveryNDays(text: string): ParsedRecurrence | null {
  // "every 2 days", "every 3 days", "var 2:a dag", "varannan dag"
  const enMatch = text.match(/^every\s+(\d+)\s*days?$/)
  if (enMatch) {
    return { mode: "FIXED", frequency: "daily", interval: parseInt(enMatch[1]!), confidence: 0.95 }
  }
  const svMatch = text.match(/^var(?:annan|\s+(\d+)(?::?[ae])?\s*dag)/)
  if (svMatch) {
    const n = svMatch[1] ? parseInt(svMatch[1]) : 2
    return { mode: "FIXED", frequency: "daily", interval: n, confidence: 0.9 }
  }
  return null
}

function tryEveryNWeeks(text: string): ParsedRecurrence | null {
  // "every 2 weeks", "varannan vecka", "var 3:e vecka"
  const enMatch = text.match(/^every\s+(\d+)\s*weeks?$/)
  if (enMatch) {
    return { mode: "FIXED", frequency: "weekly", interval: parseInt(enMatch[1]!), confidence: 0.95 }
  }
  if (/^varannan\s*vecka$/.test(text)) {
    return { mode: "FIXED", frequency: "weekly", interval: 2, confidence: 1.0 }
  }
  const svMatch = text.match(/^var\s+(\d+)(?::?[ae])?\s*vecka/)
  if (svMatch) {
    return { mode: "FIXED", frequency: "weekly", interval: parseInt(svMatch[1]!), confidence: 0.9 }
  }
  return null
}

function tryEveryNMonths(text: string): ParsedRecurrence | null {
  // "every 2 months", "quarterly", "var 3:e månad"
  if (/^quarterly|kvartalsvis$/.test(text)) {
    return { mode: "FIXED", frequency: "monthly", interval: 3, confidence: 1.0 }
  }
  const enMatch = text.match(/^every\s+(\d+)\s*months?$/)
  if (enMatch) {
    return { mode: "FIXED", frequency: "monthly", interval: parseInt(enMatch[1]!), confidence: 0.95 }
  }
  const svMatch = text.match(/^var\s+(\d+)(?::?[ae])?\s*månad/)
  if (svMatch) {
    return { mode: "FIXED", frequency: "monthly", interval: parseInt(svMatch[1]!), confidence: 0.9 }
  }
  return null
}

function trySpecificDays(text: string): ParsedRecurrence | null {
  // "every monday", "every mon, wed, fri", "every tuesday and thursday"
  // "varje måndag", "varje mån, ons, fre"
  const everyMatch = text.match(/^(?:every|varje)\s+(.+)$/)
  if (!everyMatch) return null

  const daysPart = everyMatch[1]!
  const days = parseDayList(daysPart)
  if (days.length === 0) return null

  return {
    mode: "FIXED",
    frequency: "weekly",
    interval: 1,
    weekdays: days.sort((a, b) => a - b),
    confidence: 0.9,
  }
}

function tryFlexible(text: string): ParsedRecurrence | null {
  // "3 times a week", "3x/week", "3 gånger i veckan", "2 times a month"
  const enMatch = text.match(/^(\d+)\s*(?:times?\s*(?:a|per)|x\/?)\s*(week|month)/)
  if (enMatch) {
    return {
      mode: "FIXED",
      frequency: "weekly",
      interval: 1,
      flexibleCount: parseInt(enMatch[1]!),
      flexiblePeriod: enMatch[2] === "month" ? "month" : "week",
      confidence: 0.9,
    }
  }
  const svMatch = text.match(/^(\d+)\s*gånger\s*(?:i|per)\s*(veckan|månaden)/)
  if (svMatch) {
    return {
      mode: "FIXED",
      frequency: "weekly",
      interval: 1,
      flexibleCount: parseInt(svMatch[1]!),
      flexiblePeriod: svMatch[2] === "månaden" ? "month" : "week",
      confidence: 0.9,
    }
  }
  return null
}

function tryAfterCompletion(text: string): ParsedRecurrence | null {
  // "after 2 days", "2 days after completion", "2 dagar efter"
  const enMatch = text.match(/(?:after\s+)?(\d+)\s*days?\s*(?:after(?:\s*completion)?)?/)
  if (enMatch && /after/.test(text)) {
    return {
      mode: "AFTER_COMPLETION",
      frequency: "daily",
      interval: 1,
      afterCompletionDays: parseInt(enMatch[1]!),
      confidence: 0.85,
    }
  }
  const svMatch = text.match(/(\d+)\s*dagar?\s*efter/)
  if (svMatch) {
    return {
      mode: "AFTER_COMPLETION",
      frequency: "daily",
      interval: 1,
      afterCompletionDays: parseInt(svMatch[1]!),
      confidence: 0.85,
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// Day name parsing
// ---------------------------------------------------------------------------

const DAY_MAP: Record<string, number> = {
  // English
  monday: 0, mon: 0, mo: 0,
  tuesday: 1, tue: 1, tu: 1,
  wednesday: 2, wed: 2, we: 2,
  thursday: 3, thu: 3, th: 3,
  friday: 4, fri: 4, fr: 4,
  saturday: 5, sat: 5, sa: 5,
  sunday: 6, sun: 6, su: 6,
  // Swedish
  måndag: 0, mån: 0,
  tisdag: 1, tis: 1,
  onsdag: 2, ons: 2,
  torsdag: 3, tor: 3,
  fredag: 4, fre: 4,
  lördag: 5, lör: 5,
  söndag: 6, sön: 6,
}

function parseDayList(text: string): number[] {
  // Split by comma, "and", "och", whitespace
  const parts = text.split(/[,&]+|\s+and\s+|\s+och\s+|\s+/).map((s) => s.trim()).filter(Boolean)
  const days: number[] = []

  for (const part of parts) {
    const day = DAY_MAP[part]
    if (day != null && !days.includes(day)) {
      days.push(day)
    }
  }

  return days
}
